<template lang="pug">
div
  template(v-if="isFetchingTreatments || isFetchingActiveMedication")
    v-skeleton-loader.px-1(type="list-item-three-line")
  template(v-else)
    treatment-list-edit-form(v-if="isEdit" :treatment="treatment" @updated="updateTreatment")
    div.px-5.py-4(v-else @click="editTreatment" :class="{ 'clickable': editable }")
      div.d-flex.align-center
        div.text-xs.font-semibold.neutral700--text.ellipsis-text {{ treatment.name }}
        template(v-if="treatment.product.medication && treatment.product.medication.id")
          .yz-regular-body-2xs.text-neutral-500.ml-2.no-wrap(v-if="treatment.product.sku") {{ treatment.product.sku }}
          yz-badge.ml-2(v-if="treatment.product.supplier" color="neutral-200" text-color="neutral-700" border border-color="neutral-300") {{ treatment.product.supplier }}
        y-chip.ml-2(v-if="checkActiveMadication(treatment)" label="Active" size="xs" chip-class="pa-1" color="green")
          template(#prepend)
            v-avatar.mr-1(size="6" color="success500")
        v-spacer
        div.text-xs.font-medium.neutral700--text.no-wrap.ml-3 {{ (treatment.price * treatment.quantity) | displayPrice($currency) }}
      div.d-flex.mt-1
        div
          div.text-xs.neutral700--text {{ getAmountPrice(treatment) }}
          div.text-xs.neutral500--text.mt-1(v-if="Object.keys(treatment.extra_data).length") {{ getTreatmentExtraData(treatment.extra_data) }}
        template(v-if="showEditDocument(treatment)")
          v-spacer
          div(v-if="treatment.extra_data.status === 'incomplete'" @click.stop)
            y-chip(label="Incomplete" size="xs" chip-class="pa-1 font-medium" color="red")
              template(#prepend)
                v-avatar.mr-1(size="6" color="danger500")
              template(#append)
                y-button.ml-2(label="Continue" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" unset-min-width size="xxs" height="24" button-class="px-2" :loading="isLoading" @click="openAddMedicalDocumentDialog")
          div(v-else @click.stop)
            y-chip(label="Completed" size="xs" chip-class="pa-1 font-medium" color="blue")
              template(#prepend)
                v-avatar.mr-1(size="6" color="info500")
              template(#append)
                y-button.ml-2(label="Edit" outlined color="neutral400" background-color="var(--v-neutral100)" text-color="neutral700" unset-min-width size="xxs" height="24" button-class="px-2" :loading="isLoading" @click="openAddMedicalDocumentDialog")
        
      div.mt-1(v-if="treatment.product.type === 'bundle'")
        template(v-for="(bundle, idx) in treatment.bundles")
          div.d-flex(:key="`bn-${itemIndex}-${idx}`")
            v-icon.ml-2.mr-1(size="14" color="neutral700") $yzi-corner-down-right
            div
              div.text-xxs.font-medium.neutral700--text.ellipsis-text {{ bundle.name }}
                span.ml-1(v-if="bundle.quantity_per_set > 1") x{{ bundle.quantity_per_set }}
              div.text-xxs.neutral500--text(v-if="Object.keys(bundle.extra_data).length") {{ getTreatmentExtraData(bundle.extra_data) }}
  
  .dialogs
    add-medical-document-dialog(v-if="showAddMedicalDocumentDialog" :open.sync="showAddMedicalDocumentDialog" :treatment="documentTreatment" :diagnoses="consultation.diagnoses" @updated="updateDocument")
    edit-not-allowed-dialog(v-if="showEditNotAllowedDialog" :open.sync="showEditNotAllowedDialog" )

</template>

<script>
import { mapGetters } from 'vuex'
import AddMedicalDocumentDialog from '@/components/clinic/consultation/AddMedicalDocumentDialog.vue'
import EditNotAllowedDialog from '@/components/clinic/consultation/EditNotAllowedDialog.vue'
import TreatmentListEditForm from '@/components/clinic/consultation/TreatmentListEditForm.vue'

export default {
  components: { AddMedicalDocumentDialog, EditNotAllowedDialog, TreatmentListEditForm },
  props: {
    treatment: {
      type: Object,
      required: true
    },
    itemIndex: {
      default: null
    }
  },
  data() {
    return {
      isFetchingTreatments: false,
      isFetchingActiveMedication: false,
      isLoading: false,
      isEdit: false,
      showAddMedicalDocumentDialog: false,
      showEditNotAllowedDialog: false,
      documentTreatment: null,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      leadPid: 'treatment/getLeadPid',
      patient: 'treatment/getPatient',
      activeMedications: 'treatment/getActiveMedications',
      consultation: 'treatment/getConsultation',
      treatmentEditingStates: 'treatment/getTreatmentEditingStates',
      isDoctor: 'user/isDoctor',
    }),
    allowDocumentEdit () {
      if (this.company.genes.find(gene => gene.feature === 'lock_document_edit_treatment_plan') && !this.isAdminOrDoctor) return false
      return true
    },
    isAdminOrDoctor () {
      if (this.isAdmin || this.isDoctor) return true
      return false
    },
    editable () {
      return this.treatment.product.type !== 'bundle'
    }
  },
  methods: {
    getAmountPrice (treatment) {
      let data = []
      let price = this.$options.filters.displayPrice(treatment.price, this.$currency)
      if (treatment.extra_data && treatment.extra_data.price_tier_name) { data.push(treatment.extra_data.price_tier_name) }
      if (treatment.quantity > 1) { data.push(`${price} x${treatment.quantity}`) }
      else { data.push(price) }
      return data.join(', ')
    },
    getTreatmentExtraData (extra_data) {
      let data = []
      if (extra_data.indication) { data.push(extra_data.indication) }
      if (extra_data.dosage_value && extra_data.dosage_unit) { data.push(`${extra_data.dosage_value} ${extra_data.dosage_unit}`) }
      if (extra_data.frequency) { data.push(extra_data.frequency) }
      if (extra_data.duration) { data.push(extra_data.duration) }
      if (extra_data.instruction) { data.push(extra_data.instruction) }
      if (extra_data.precaution) { data.push(extra_data.precaution) }
      return data.join(' | ')
    },
    checkActiveMadication (treatment) {
      if (treatment.product.medication && treatment.product.medication.id) {
        if (this.activeMedications.find(f => f.m_id === treatment.product.medication.id)) {
          return true
        }
      }
      return false
    },
    showEditDocument (treatment) {
      if (treatment.product.type === 'documents') {
        return true
      }
      return false
    },
    openAddMedicalDocumentDialog () {
      if (!this.allowDocumentEdit) {
        this.showEditNotAllowedDialog = true
        return
      }
      let treatment = {
        item_id: this.treatment.id || null,
        id: this.treatment.product.id,
        name: this.treatment.product.name,
        inventory_enabled: this.treatment.product.inventory_enabled,
        inventory_qty: this.treatment.product.inventory_qty,
        type: this.treatment.product.type,
        price: this.treatment.product.price,
        medication: this.treatment.product.medication,
        document_template: this.treatment.product.document_template,
        document: { ...this.treatment.extra_data }
      }
      this.documentTreatment = treatment
      this.showAddMedicalDocumentDialog = true
    },
    updateDocument (data) {
      this.isLoading = true
      let payload = { extra_data: { ...this.documentTreatment.extra_data, ...data } }
      const endpoint = `/clinics/${this.company.slug}/invoices/${this.leadPid}`
      this.$axios.patch(endpoint, { products: [{ id: this.treatment.id, ...payload }] })
        .then(resp => {
          this.updateActivityLog(endpoint, payload, resp.data, 'PATCH', resp.data.id, 'consultation', `Update document (${this.leadPid})`)
          this.$emit('updated')
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')   
          if (err.response.data) {
            if (err.response.data.detail) {
              message = err.response.data.detail
            } else {
              let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
              if (data) { message = data }
            }
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.showAddMedicalDocumentDialog = false
          this.isLoading = false
        })
    },
    editTreatment () {
      if (this.editable) {
        this.isEdit = true
        let states = this.treatmentEditingStates
        states.push(this.treatment)
        this.$store.commit('treatment/updateTreatmentEditingStates', states)
      }
    },
    updateTreatment () {
      this.fetchTreatments()
      this.fetchActiveMedication()
      this.isEdit = false
      let states = this.treatmentEditingStates.filter(treatment => treatment.id !== this.treatment.id)
      this.$store.commit('treatment/updateTreatmentEditingStates', states)
    },
    fetchTreatments () {
      this.isFetchingTreatments = true
      let params = {
        fields: 'treatments'
      }
      this.$axios.get(`/clinics/${this.company.slug}/consultations/${this.leadPid}`, { params })
        .then(resp => {
          this.$store.commit('treatment/setTreatments', resp.data.treatments)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingTreatments = false
        })
    },
    fetchActiveMedication () {
      this.isFetchingActiveMedication = true
      const params = {
        page_size: -1,
        patient: this.patient.id,
        expand: 'medication'
      }
      this.$axios.get(`/clinics/${this.company.slug}/active_medications`, { params })
        .then(resp => {
          let activeMedications = []
          if (resp.data && resp.data.results) {
            activeMedications = resp.data.results.map(m => { return { id: m.id, m_id: m.medication.id, name: m.medication.name } })
          }
          this.$store.commit('treatment/setActiveMedications', activeMedications)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingActiveMedication = false
        })
    },
    updateActivityLog (endpoint, payload, response, method, id, objectRef, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: objectRef,
        activity: activity,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    updateReadState () {
      this.isEdit = false
    }
  },
}
</script>

<style lang="scss" scoped>
.ellipsis-text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}
</style>